import React, { useCallback, useEffect, useMemo } from 'react';
import { Form } from 'react-bootstrap';

import { useAppSelector, useAppDispatch } from 'redux/store';
import { getWorkersPaginated, Worker } from '../../redux/slices/workersSlice';

// A worker once it has been selected and assigned a priority. Built by
// spreading an available Worker, so it carries the full Worker shape plus
// priority — priority belongs to the selection, not the available worker.
export interface PrioritizedWorker extends Worker {
  priority: number;
}

interface WorkerListWithPriorityProps {
  selectedWorkers: PrioritizedWorker[]; // Workers the user has selected, each with a priority
  setSelectedWorkers: (workers: PrioritizedWorker[]) => void; // Callback to update the selected workers
}

interface WorkerPriorityRowProps {
  worker: Worker;
  selectedWorkers: PrioritizedWorker[];
  setSelectedWorkers: (workers: PrioritizedWorker[]) => void;
}

const WorkerPriorityRow: React.FC<WorkerPriorityRowProps> = ({ worker, selectedWorkers, setSelectedWorkers }) => {
  // Resolve the selection once; `checked`, `value`, and both handlers all
  // derive from it, so the row stays symmetric whether or not it's selected.
  const selected = useMemo(() => selectedWorkers.find((w) => w.worker_id === worker.worker_id), [selectedWorkers, worker.worker_id]);

  const handleToggle = useCallback(
    (isChecked: boolean) => {
      if (isChecked) {
        setSelectedWorkers([...selectedWorkers, { ...worker, priority: 0 }]);
      } else {
        setSelectedWorkers(selectedWorkers.filter((w) => w.worker_id !== worker.worker_id));
      }
    },
    [selectedWorkers, setSelectedWorkers, worker]
  );

  const handlePriorityChange = useCallback(
    (priority: number) => {
      setSelectedWorkers(selectedWorkers.map((w) => (w.worker_id === worker.worker_id ? { ...w, priority } : w)));
    },
    [selectedWorkers, setSelectedWorkers, worker.worker_id]
  );

  return (
    <div className="mb-2">
      <Form.Check type="checkbox" label={worker.worker_name} checked={!!selected} onChange={(e) => handleToggle(e.target.checked)} />
      {selected && (
        <Form.Control
          type="number"
          value={selected.priority}
          onChange={(e) => handlePriorityChange(parseInt(e.target.value, 10) || 0)}
          placeholder="Priority"
        />
      )}
    </div>
  );
};

const WorkerListWithPriority: React.FC<WorkerListWithPriorityProps> = ({ selectedWorkers, setSelectedWorkers }) => {
  const dispatch = useAppDispatch();
  const workers = useAppSelector((state) => state.workers.items);

  useEffect(() => {
    dispatch(getWorkersPaginated({}) as any);
  }, [dispatch]);

  return (
    <Form.Group controlId="workers">
      <Form.Label>Workers</Form.Label>
      {workers.map((worker) => (
        <WorkerPriorityRow key={worker.worker_id} worker={worker} selectedWorkers={selectedWorkers} setSelectedWorkers={setSelectedWorkers} />
      ))}
    </Form.Group>
  );
};

export default WorkerListWithPriority;
